/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * @format
 * @flow
 */

import Axios, { AxiosInstance } from 'axios';
import WebSocketController from '../../controllers/web-socket-controller';
import subscriberEnum from '../../enums/subscriberEnum';

/**
 * Supported socket operations
 */

const AUTHENTICATE = 'authenticate';
const GET_STORE_MENU = 'getStoreMenu';
const GET_STORE_CHECKS = 'getStoreChecks';
const GET_STORE_NOTIFICATIONS = 'getStoreNotifications';
const GET_STORE_BOOKINGS = 'getStoreBookings';
const SUBSCRIBE_TO_STORE = 'subscribeToStore';
const UNSUBSCRIBE_TO_STORE = 'unsubscribeToStore';
const SUBSCRIBE_TO_STORE_MENU = 'subscribeToStoreMenu';
const SUBSCRIBE_TO_STORE_CHECKS = 'subscribeToStoreChecks';
const SUBSCRIBE_TO_STORE_NOTIFICATIONS = 'subscribeToStoreNotifications';
const UNSUBSCRIBE_TO_STORE_MENU = 'unsubscribeToStoreMenu';
const UNSUBSCRIBE_TO_STORE_CHECKS = 'unsubscribeToStoreChecks';
const UNSUBSCRIBE_TO_STORE_NOTIFICATIONS = 'unsubscribeToStoreNotifications';
const SUBSCRIBE_TO_STORE_BOOKINGS = 'subscribeToStoreBookings';
const UNSUBSCRIBE_TO_STORE_BOOKINGS = 'unsubscribeToStoreBookings';

class RedisDBService {
  static socket: WebSocketController;

  static listenerMap = {};

  static axiosInstance: AxiosInstance;

  static url: String;

  static addListener = (event, callback) => {
    if (!RedisDBService.listenerMap[event]) {
      RedisDBService.listenerMap[event] = [];
    }

    const index = RedisDBService.listenerMap[event].push(callback) - 1;
    return () => {
      console.info(`removing event ${event}`);
      RedisDBService.listenerMap[event].splice(index, 1);
    };
  };

  static createWebSocket = (url, callback, disconnectCallback) => {
    RedisDBService.url = `${url}/iapi/connect`;
    console.info('connecting to url', RedisDBService.url);
    if (RedisDBService.socket) {
      RedisDBService.close();
    }
    RedisDBService.socket = new WebSocketController(
      RedisDBService.url,
      callback,
      disconnectCallback,
    );
    console.info('socket', RedisDBService.socket);
  };

  static initialize = (cacheHostname, storeId, headers, callback) => {
    RedisDBService.axiosInstance = Axios.create({
      baseURL: `https://${cacheHostname}`,
      timeout: 10000,
      headers,
    });
    RedisDBService.socket.sendMessageUncompressed(
      AUTHENTICATE,
      storeId,
      headers,
      (error, response) => {
        if (error) {
          callback(error, null);
        } else {
          const listeners = this.listenerMap.initialize;
          if (listeners) {
            listeners.forEach(listener => {
              listener && listener();
            });
          }
          callback(null, response);
        }
      },
    );
  };

  static ping = callback => {
    RedisDBService.axiosInstance
      .get(`https://${RedisDBService.url}/ping`)
      .then(response => {
        console.info('ping response', response);
        callback(null, response.data);
      })
      .catch(error => {
        console.warn('ping error', error);
        callback(error);
      });
  };

  static getStoreMenu = (storeId: number, callback: Function) => {
    RedisDBService.socket.sendMessage(GET_STORE_MENU, storeId, callback);
  };

  static getStoreChecks = (storeId: number, callback: Function) => {
    RedisDBService.socket.sendMessage(GET_STORE_CHECKS, storeId, callback);
  };

  static getStoreBookings = (storeId: number, callback: Function) => {
    RedisDBService.socket.sendMessage(GET_STORE_BOOKINGS, storeId, callback);
  };

  static getEmployeeChecks = (storeId: number, employeeId: number, callback: Function) => {
    console.info('callback', callback);
    RedisDBService.getStoreChecks(storeId, (error, response) =>
      callback(error, response ? response.filter(each => each.employeeId === employeeId) : null),
    );
  };

  static getStoreNotifications = (storeId: number, callback: Function) => {
    console.info('getStoreNotifications', storeId);
    RedisDBService.socket.sendMessage(GET_STORE_NOTIFICATIONS, storeId, callback);
  };

  static getEmployeeNotifications = (storeId: number, employeeId: number, callback: Function) => {
    RedisDBService.getStoreNotifications(storeId, (error, response) => callback(error, response ? response.filter(each => each.employeeId === employeeId) : null),
    );
  };

  static subscribeToMenuUpdate = (storeId, callback: Function, subscriber: Function) => {
    console.info('subscribeToMenuUpdate', storeId, callback, subscriber);
    let id = null;
    RedisDBService.socket.redisSubscribe(
      SUBSCRIBE_TO_STORE_MENU,
      storeId,
      (error, response) => {
        if (!error) {
          id = response.id;
        }
        callback(error, response);
      },
      subscriber,
    );
    return () => {
      RedisDBService.socket.sendMessage(UNSUBSCRIBE_TO_STORE_MENU, id, () => {
        RedisDBService.socket.unsubscribe(id);
      });
    };
  };

  static subscribeToStore = (storeId, callback: Function, subscriber: Function) => {
    let id = null;
    RedisDBService.socket.redisSubscribe(
      SUBSCRIBE_TO_STORE,
      storeId,
      (error, response) => {
        if (!error) {
          id = response.id;
        }
        callback(error, response);
      },
      subscriber,
    );
    return () => {
      RedisDBService.socket.sendMessage(UNSUBSCRIBE_TO_STORE, id, () => {
        RedisDBService.socket.unsubscribe(id);
      });
    };
  };

  static subscribeToStoreChecks = (storeId, callback: Function, subscriber: Function) => {
    let id = null;
    RedisDBService.socket.redisSubscribe(
      SUBSCRIBE_TO_STORE_CHECKS,
      storeId,
      (error, response) => {
        if (!error) {
          id = response.id;
        }
        callback(error, response);
      },
      subscriber,
    );
    return () => {
      RedisDBService.socket.sendMessage(UNSUBSCRIBE_TO_STORE_CHECKS, id, () => {
        RedisDBService.socket.unsubscribe(id);
      });
    };
  };

  static subscribeToStoreBookings = (storeId, callback: Function, subscriber: Function) => {
    let id = null;
    RedisDBService.socket.redisSubscribe(
      SUBSCRIBE_TO_STORE_BOOKINGS,
      storeId,
      (error, response) => {
        if (!error) {
          id = response.id;
        }
        callback(error, response);
      },
      subscriber,
    );
    return () => {
      RedisDBService.socket.sendMessage(UNSUBSCRIBE_TO_STORE_BOOKINGS, id, () => {
        RedisDBService.socket.unsubscribe(id);
      });
    };
  };

  static subscribeToEmployeeChecks = (storeId, employeeId, callback, subscriber) => RedisDBService.subscribeToStoreChecks(storeId, callback, response => {
      if (response.type === subscriberEnum.DELETE) {
        subscriber(response);
      } else if (response.value.employeeId === employeeId) {
        subscriber(response);
      }
    });

  static subscribeToStoreNotifications = (storeId, callback: Function, subscriber: Function) => {
    let id = null;
    RedisDBService.socket.redisSubscribe(
      SUBSCRIBE_TO_STORE_NOTIFICATIONS,
      storeId,
      (error, response) => {
        if (!error) {
          id = response.id;
        }
        callback(error, response);
      },
      subscriber,
    );
    return () => {
      RedisDBService.socket.sendMessage(UNSUBSCRIBE_TO_STORE_NOTIFICATIONS, id, () => {
        RedisDBService.socket.unsubscribe(id);
      });
    };
  };

  static subscribeToEmployeeNotifications = (storeId, employeeId, callback, subscriber) => RedisDBService.subscribeToStoreNotifications(storeId, callback, response => {
      console.info('subscribeToEmployeeNotifications response', response);
      if (response.type === subscriberEnum.DELETE) {
        subscriber(response);
      } else if (response.value.employeeId === employeeId) {
        subscriber(response);
      }
    });

  static close = () => {
    RedisDBService.socket.close();
  };
}

export default RedisDBService;
